// SKE-3427 — shared section-header primitive for /app/report sections
// (and SharedReportPage). Owns the canonical eyebrow + accent bar + h2
// + optional description treatment so every section reads as one
// coherent design pattern.
//
// Eyebrow style follows the new Sked design canon: Quarto italic
// sentence case in the brand-purple. The short blurple accent bar
// between eyebrow and title is preserved per Hugh's
// `feedback_section_eyebrow_accent` memory ("short bar BETWEEN
// eyebrow and title, not a rule below the whole heading").
//
// Title accepts a `ReactNode` so callers can wrap one word in a
// `<em className="font-serif text-purple-700">…</em>` for the Sked
// signature highlight. This component does not enforce the highlight
// — it's the caller's editorial choice which word matters.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface SectionHeaderProps {
  /** Required short label above the title. Sentence case — the
   *  primitive renders Quarto italic, no caller-side casing needed. */
  eyebrow: string;
  /** H2 content. Pass a `<>…</>` fragment when wrapping a highlight
   *  word, e.g. `<>How <em className="font-serif text-purple-700">deeply</em> social visitors explore</>`. */
  title: ReactNode;
  /** Optional descriptive paragraph below the title — capped at ~60ch
   *  on the consumer side via `max-w-[60ch]` already; here we just
   *  render whatever ReactNode the caller passes. */
  description?: ReactNode;
  /** Optional extra class for the wrapper. Sections that need to add
   *  e.g. `space-y-4` siblings can extend without rewrapping. */
  className?: string;
  /** Heading level — defaults to h2. Sub-section headers under a
   *  taxonomy or recommendations card may want h3. */
  as?: "h2" | "h3";
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  className,
  as = "h2",
}: SectionHeaderProps) {
  const Heading = as;
  return (
    <div className={cn(className)}>
      <p className="font-serif text-[17px] italic font-normal text-purple-700">{eyebrow}</p>
      <span aria-hidden="true" className="mt-1.5 block h-[3px] w-8 rounded-full bg-purple-500" />
      <Heading className="mt-3 font-display text-2xl font-semibold text-neutral-950">
        {title}
      </Heading>
      {description ? (
        <p className="mt-2 max-w-[60ch] text-sm text-neutral-600 text-pretty">{description}</p>
      ) : null}
    </div>
  );
}
